import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { InputDateTimev2 } from "@itandi/itandi-bb-ui/InputDateTimev2";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import type { Deal } from "~/models/Deal";
import { isOwnStatus } from "~/models/Status";
import { isSuccess } from "~/utils/api/api_handler";
import { update } from "~/utils/api/DealInspection";
import { toDate } from "~/utils/DateUtil";
import { InspectionCandidateDatesList } from "../inspection_candidate_dates/InspectionCandidateDatesList";

type Props = {
	deal: Deal;
	handleClose: () => void;
	handleMutate: () => Promise<void>;
	isOpen: boolean;
};

type VisitScheduleInputParam = {
	inspectionDate: Date | null;
	surveyDate: Date | null;
};

/**
 * 立会日程・現地調査日程登録モーダル
 *
 * @for client
 */

export const ScheduleInputModal: React.FC<Props> = ({
	deal,
	isOpen,
	handleClose,
	handleMutate
}: Props) => {
	const ownManagement = isOwnStatus(deal.statusId);
	const [syncInspectionDate, setSyncInspectionDate] = React.useState(true);
	const { watch, formState, handleSubmit, setValue } =
		useForm<VisitScheduleInputParam>({
			defaultValues: {
				inspectionDate: toDate(deal.inspectionDate),
				surveyDate: toDate(deal.surveyDate)
			}
		});
	const inspectionDate = watch("inspectionDate");
	const surveyDate = watch("surveyDate");
	const onClickSubmit = React.useCallback(() => {
		handleSubmit(async () => {
			const response = await update(deal.id, inspectionDate, surveyDate);
			if (isSuccess(response)) {
				await handleMutate();
				toast.success("追加しました");
				handleClose();
			} else {
				toast.error("エラーが発生しました");
			}
		})();
	}, [
		deal,
		handleClose,
		handleMutate,
		handleSubmit,
		inspectionDate,
		surveyDate
	]);
	return (
		<Modalv2 isOpen={isOpen} onClose={handleClose}>
			<ModalHeader
				title={`${ownManagement ? "立会日程・現地調査日程" : "立会日程"}登録`}
			/>
			<ModalBody>
				<InspectionCandidateDatesList deal={deal} />
				<FormGroup variant="vertical">
					{ownManagement || deal.inspectionBy === "company" ? (
						<FormControl label="立会実施日程">
							<InputDateTimev2
								onChange={(date): void => {
									setValue("inspectionDate", date);
									if (ownManagement && syncInspectionDate) {
										setValue("surveyDate", date);
									}
								}}
								value={inspectionDate}
							/>
						</FormControl>
					) : null}
					{ownManagement ? (
						<FormControl label="現地調査実施日程">
							<InputCheckBox
								checked={syncInspectionDate}
								label="立会実施日程と同じ値で補完"
								onChange={(e): void => {
									setSyncInspectionDate(e.target.checked);
									if (e.target.checked) {
										setValue("surveyDate", inspectionDate);
									}
								}}
							/>
							<InputDateTimev2
								disabled={ownManagement ? syncInspectionDate : undefined}
								marginTop={8}
								onChange={(date): void => {
									setValue("surveyDate", date);
								}}
								value={surveyDate}
							/>
						</FormControl>
					) : null}
				</FormGroup>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						onClick={(): void => {
							handleClose();
						}}
						variant="secondary"
					>
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						disabled={formState.isSubmitting}
						onClick={onClickSubmit}
						variant="primary"
					>
						確定
					</Button>
				}
			/>
		</Modalv2>
	);
};
